import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { Pill } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel, deliveryStatus } from '../../lib/format';
import { DeliveryEditor } from './DeliveryEditor';
import { DeliveryDetail } from './DeliveryDetail';
import type { Delivery } from '../../lib/types';

export function DeliveriesPage() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const canWrite = can(user, 'deliveries', 'create');
  const [reloadSignal, setReloadSignal] = useState(0);
  const [editor, setEditor] = useState<{ mode: 'new' } | { mode: 'edit'; d: Delivery } | null>(null);

  const refresh = () => setReloadSignal((n) => n + 1);

  const columns: Column<Delivery>[] = [
    { header: '交货单号', render: (d) => <span className="num strong">{d.number}</span> },
    { header: '客户', render: (d) => d.customer_name || '—' },
    { header: '来源合同', render: (d) => <span className="num muted">{d.contract_number || '—'}</span> },
    { header: '交货日期', render: (d) => <span className="num">{dateLabel(d.delivery_date)}</span> },
    { header: '承运人', render: (d) => d.carrier || '—' },
    { header: '来源', render: (d) => (d.legacy_erpnext_id ? <span className="muted">ERPNext</span> : <span className="muted">系统内建</span>) },
    { header: '状态', render: (d) => { const s = deliveryStatus(d.status); return <Pill tone={s.tone} label={s.label} />; } },
  ];

  return (
    <>
      {editor ? (
        <DeliveryEditor
          initial={editor.mode === 'edit' ? editor.d : null}
          onCancel={() => setEditor(null)}
          onSaved={(saved) => {
            setEditor(null);
            refresh();
            const url = new URL(window.location.href);
            url.searchParams.set('q', saved.number);
            window.history.replaceState({}, '', url.toString());
          }}
        />
      ) : null}

      <ListPage<Delivery>
        title="交货单"
        desc="交货单 · /api/deliveries：从合同剩余数量生成、草稿编辑与确认/发出/送达/取消流转、批次与序列号一致性校验、确认后回写合同已交数量与进度、审计时间线，以及西语 REMITO / PDF（含 ERPNext 历史只读单，当页展开，不用抽屉）"
        endpoint="/api/deliveries"
        columns={columns}
        rowId={(d) => d.id}
        searchPlaceholder="搜索交货单号 / 状态 / 承运人 / 运单号 / 合同 / 客户…"
        initialQuery={sp.get('q') || ''}
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="deliveries" row={row} fields={RECORD_FIELDS['deliveries']} label={String(row.number)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setEditor((e) => (e && e.mode === 'new' ? null : { mode: 'new' }))}>
            {editor?.mode === 'new' ? '收起新建' : '新建交货单'}
          </button>
        ) : undefined}
        renderDetail={(row) => (
          <DeliveryDetail
            row={row}
            onEdit={(d) => setEditor((e) => e ?? { mode: 'edit', d })}
            onChanged={refresh}
          />
        )}
      />
      {!canWrite ? (
        <div className="notice info" style={{ marginTop: 'var(--gap)' }}>
          当前角色为只读。交货单的创建、编辑与状态流转由销售 / 仓储 / 管理员执行（服务端强制校验超交、重复交付与角色权限）；西语打印 / 导出 REMITO 仅限管理员 / 销售 / 仓库角色（含 ERPNext 历史交货单）。
        </div>
      ) : null}
    </>
  );
}
